<template>
  <div>
    <ListSelect
      ref="listSelect"
      class="w-[40ch] pb-1"
      placeholder="Enter search criteria"
      multiple
      :options="tagsList"
      v-model="searchTerms"
    />

    <ul
      v-if="searchResults.length > 0"
      class="text-sm list-[circle] pl-6 pt-1 max-h-60 overflow-auto"
    >
      <li
        v-for="(loc, locid) in searchResults"
        :key="locid"
        class="link"
        @click="showLocation(loc)"
      >
        At layer {{ loc.layer }}, row {{ loc.row }}, column
        {{ loc.column }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { assertUnreachable } from "@/utils";
import {
  ActionInternal,
  isCustom,
  isCustomConsumer,
  isCustomFirmware,
  isCustomLed,
  isCustomLedBrightness,
  isCustomLedCycle,
  isCustomMouse,
  isCustomMouseClick,
  isCustomMouseMove,
  isCustomMouseSensitivity,
  isDefaultLayer,
  isHoldTapInternal,
  isKeyCode,
  isLayer,
  isMultipleActionsInternal,
  isMultipleKeyCodes,
  isNoOp,
  isTrans,
  Location,
} from "@/keyboard";
import { useKeyboardStore, useHighlightStore } from "@/stores";
import ListSelect from "@/components/utils/ListSelect.vue";

const emit = defineEmits<{
  (_e: "searchChanged", _terms: string[]): void;
  (_e: "highlightChanged", _enabled: boolean): void;
}>();

type Loc = [number, number, number];

const kb = useKeyboardStore();
const hl = useHighlightStore();

const listSelect = ref<InstanceType<typeof ListSelect> | null>(null);
const searchTerms = ref<string[]>([]);

watch(searchTerms, () => emit("searchChanged", searchTerms.value));

// Generate list of "search tags" associated with given action
function getActionTags(a: ActionInternal): string[] {
  const tags: string[] = [];
  if (isNoOp(a)) {
    tags.push("NoOp");
  } else if (isTrans(a)) {
    tags.push("Transparent");
  } else if (isKeyCode(a)) {
    tags.push("KeyCode", a.KeyCode);
  } else if (isMultipleKeyCodes(a)) {
    tags.push("MultipleKeyCodes", ...a.MultipleKeyCodes);
  } else if (isMultipleActionsInternal(a)) {
    tags.push(...a.MultipleActions.flatMap(getActionTags));
  } else if (isLayer(a)) {
    tags.push("Layer", a.Layer.toString());
  } else if (isDefaultLayer(a)) {
    tags.push("DefaultLayer", a.DefaultLayer.toString());
  } else if (isHoldTapInternal(a)) {
    tags.push(
      "HoldTap",
      ...getActionTags(a.HoldTap.tap),
      ...getActionTags(a.HoldTap.hold)
    );
  } else if (isCustom(a)) {
    tags.push("Custom");
    if (isCustomLed(a.Custom)) {
      tags.push("Led");
      const led = a.Custom.Led;
      tags.push(
        isCustomLedBrightness(led)
          ? "Brightness"
          : isCustomLedCycle(led)
          ? "Cycle"
          : assertUnreachable(led)
      );
    } else if (isCustomMouse(a.Custom)) {
      tags.push("Mouse");
      const mouse = a.Custom.Mouse;
      tags.push(
        isCustomMouseMove(mouse)
          ? "Move"
          : isCustomMouseClick(mouse)
          ? "Click"
          : isCustomMouseSensitivity(mouse)
          ? "Sensitivity"
          : assertUnreachable(mouse)
      );
    } else if (isCustomConsumer(a.Custom)) {
      tags.push("Media", a.Custom.Consumer);
    } else if (isCustomFirmware(a.Custom)) {
      tags.push("Firmware", a.Custom.Firmware);
    } else {
      assertUnreachable(a.Custom);
    }
  } else {
    assertUnreachable(a);
  }
  return tags;
}

const tagLocations = computed(() => {
  const pairs: [Loc, string[]][] = [];
  // Generate all pairs <position(layer, row, col), action_strings>
  kb.config.layers.forEach((layer, l) =>
    layer.forEach((row, r) =>
      row.forEach((col, c) => {
        pairs.push([[l, r, c], getActionTags(col)]);
      })
    )
  );
  // Create a mapping from action_string to all positions to which it applies
  const map: Record<string, Loc[]> = {};
  pairs.forEach(([pos, strings]) =>
    strings.forEach((s) => {
      map[s] = map[s] ?? [];
      const contains = map[s].find(
        ([l, r, c]) => l == pos[0] && r == pos[1] && c == pos[2]
      );
      if (contains == undefined) map[s].push(pos);
    })
  );
  return map;
});

const tagsList = computed(() => {
  return Object.keys(tagLocations.value);
});

function locationsIntersection(sets: Loc[][]): Loc[] {
  if (sets.length == 0) return [];
  if (sets.length == 1) return sets[0];

  const [head, tail] = [sets[0], sets.slice(1)];
  return head.filter(([layer, row, col]) =>
    tail.every(
      (tagLocations) =>
        !!tagLocations.find(([l, r, c]) => l == layer && r == row && c == col)
    )
  );
}

const searchResults = computed(() => {
  if (searchTerms.value.length == 0) return [];
  const locationSets = searchTerms.value.map(
    (term) => tagLocations.value[term]
  );
  return locationsIntersection(locationSets).map(([layer, row, column]) => ({
    layer,
    row,
    column,
  }));
});

const showLocation = (loc: Location) => {
  kb.setLayer(loc.layer);

  const duration = 1000; // ms
  hl.setHighlightedLayer(loc.layer, duration);
  hl.setHighlightedKey(loc.row, loc.column, duration);

  emit("highlightChanged", true);
  setTimeout(() => emit("highlightChanged", false), duration);
};
</script>
